import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import React from 'react';
import { COMPLIANCE_COLOR } from '../pages/store-details/components/PlanogramComplianceControlCenter';
import { BBox, BoxAction, CameraPog, PogBBox } from '../types';
import { ImageBox } from './BoxedImage';
import { ImageViewer } from './ImageViewer';

interface CameraImageViewerProps {
  // Camera image props
  image?: HTMLImageElement;
  preloadedImage?: string;
  fallbackPogBoxColor?: string;
  selectedPogBoxColor?: string;
  highlightedIlItemId?: number;
  filteredBboxes: BBox[];
  boxActions: BoxAction[];
  productRecognitionDetections: ImageBox[];
  planogramComplianceActions: ImageBox[];
  showProductImages: boolean;
  showFallbackImages?: boolean;
  chainId?: number;
  onBoxClick: (bbox: BBox) => void;
  onProductRecognitionClick?: (detection: ImageBox) => void;
  onPlanogramComplianceClick?: (detection: ImageBox) => void;
  titleActions?: React.ReactNode;
  onBoxesSelected?: (selectedBboxes: BBox[]) => void;
  onBoxRightClick?: (detection: PogBBox | ImageBox) => void;

  // POG images props
  showPogs: boolean;
  currentPogs: CameraPog[];
  preloadedImages?: Record<string, HTMLImageElement>;
  preloadedImagesB64: Record<string, string>;
  selectedIlItemIds: Set<number>;
  planogramStatusMap: Map<number, string>;
  onPogBoxClick: (bbox: PogBBox) => void;
  onPogBoxRightClick?: (bbox: PogBBox) => void;
  showShelfColumnLabels?: boolean;
  showEmbeddingsCount?: boolean;
  imageBackgroundColor?: string;
  showImageFrameBorder?: boolean;
  fitCameraToContainerWidth?: boolean;
  maxCameraImageHeight?: number;
  enableCameraPanZoom?: boolean;
  resetCameraTransformOnResize?: boolean;
  cameraTransformResetKey?: string | number;
  pogHeader?: React.ReactNode;
  pogHeaderActions?: React.ReactNode;
}

export const CameraImageViewer: React.FC<CameraImageViewerProps> = (props) => {
  const {
    // Camera image props
    image,
    preloadedImage,
    fallbackPogBoxColor,
    filteredBboxes,
    highlightedIlItemId,
    boxActions,
    productRecognitionDetections,
    planogramComplianceActions,
    showProductImages,
    showFallbackImages,
    chainId,
    onBoxClick,
    onProductRecognitionClick,
    onPlanogramComplianceClick,
    titleActions,
    onBoxesSelected,
    onBoxRightClick,

    // POG images props
    showPogs,
    currentPogs,
    preloadedImages,
    preloadedImagesB64,
    selectedIlItemIds,
    planogramStatusMap,
    onPogBoxClick,
    onPogBoxRightClick,
    showShelfColumnLabels = false,
    showEmbeddingsCount = false,
    imageBackgroundColor = '#f9fafb',
    showImageFrameBorder = true,
    fitCameraToContainerWidth = false,
    maxCameraImageHeight,
    enableCameraPanZoom = true,
    resetCameraTransformOnResize = false,
    cameraTransformResetKey,
    pogHeader,
    pogHeaderActions,
  } = props;
  const selectedPogBoxColor = props.selectedPogBoxColor || '#2563eb';
  const pogGridTemplateColumns =
    currentPogs.length === 4
      ? 'repeat(2, minmax(0, 1fr))'
      : currentPogs.length > 1
        ? 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))'
        : '1fr';

  return (
    <div
      className={`grid grid-cols-1 ${
        showPogs && currentPogs.length > 0 ? 'lg:grid-cols-2' : ''
      } gap-4`}
    >
      {/* Camera Image */}
      <div className="space-y-1">
        <div className="flex items-center justify-between min-h-8">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">Camera Image</h3>
            {onBoxesSelected ? (
              <Tooltip title="Hold Shift and drag to select multiple boxes" arrow>
                <InfoIcon className="text-gray-500 cursor-help" fontSize="small" />
              </Tooltip>
            ) : null}
          </div>
          {titleActions}
        </div>
        <ImageViewer<BBox>
          image={image}
          imageBase64={preloadedImage}
          bboxes={filteredBboxes}
          onBoxClick={onBoxClick}
          onBoxRightClick={onBoxRightClick}
          boxActions={boxActions}
          productRecognitionDetections={productRecognitionDetections}
          onProductRecognitionClick={onProductRecognitionClick}
          planogramComplianceActions={planogramComplianceActions}
          onPlanogramComplianceClick={onPlanogramComplianceClick}
          enableZoom={enableCameraPanZoom}
          fitToContainerWidth={fitCameraToContainerWidth}
          maxDisplayHeight={maxCameraImageHeight}
          resetTransformOnSizeChange={resetCameraTransformOnResize}
          transformResetKey={cameraTransformResetKey}
          showProductImages={showProductImages}
          showFallbackImages={showFallbackImages}
          chainId={chainId}
          onBoxesSelected={onBoxesSelected}
          showShelfColumnLabels={showShelfColumnLabels}
          showEmbeddingsCount={showEmbeddingsCount}
          imageBackgroundColor={imageBackgroundColor}
          showFrameBorder={showImageFrameBorder}
        />
      </div>

      {/* POG Images - only show if there are POGs and showPogs is enabled */}
      {showPogs && currentPogs.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between min-h-8 gap-2">
            <h3 className="text-base font-semibold">
              POG Images ({currentPogs.length})
              {pogHeader && (
                <span className="ml-2 text-sm font-normal text-gray-800">{pogHeader}</span>
              )}
            </h3>
            {pogHeaderActions ? (
              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                {pogHeaderActions}
              </div>
            ) : null}
          </div>
          <div
            className="grid grid-cols-1 gap-4"
            style={{
              gridTemplateColumns: pogGridTemplateColumns,
            }}
          >
            {currentPogs.map((pog, pogIndex) => {
              const key = `${pog.ilSectionId ?? pogIndex}:${pog.pogImageUri || 'no-image'}`;
              const preloadedPogImage = pog?.pogImageUri
                ? preloadedImagesB64[pog.pogImageUri]
                : undefined;
              const preloadedPogImageElement =
                pog?.pogImageUri && preloadedImages ? preloadedImages[pog.pogImageUri] : undefined;

              // Map each pog bbox to include color and label based on compliance status
              const pogBboxesWithColor = (pog?.bboxes ?? []).map((bbox) => {
                let color = bbox.modelMatched ? '#2196F3' : (fallbackPogBoxColor ?? '#3b82f6'); // blue for model-matched, fallback otherwise

                // Check if this POG item should be highlighted
                // Since both planogram and realogram clicks now use comprehensive selection,
                // we just need to check if this item is in the selected set
                let isSelected = false;
                if (selectedIlItemIds.size > 0) {
                  isSelected = selectedIlItemIds.has(bbox.ilItemId);
                }

                // If highlighted, use blue color. Otherwise, use compliance status color
                if (highlightedIlItemId && bbox.ilItemId === highlightedIlItemId) {
                  color = '#FFFF00';
                } else if (isSelected) {
                  color = selectedPogBoxColor;
                } else if (bbox.ilItemId && planogramStatusMap.has(bbox.ilItemId)) {
                  const status = planogramStatusMap.get(bbox.ilItemId);
                  if (status && status in COMPLIANCE_COLOR) {
                    color = COMPLIANCE_COLOR[status as keyof typeof COMPLIANCE_COLOR];
                  }
                }

                // Build shelf-column label with mod index
                let shelfColumnLabel: string | undefined;
                if (bbox.shelf !== undefined && bbox.column !== undefined) {
                  // Only include mod number if there are multiple POGs
                  shelfColumnLabel =
                    currentPogs.length > 1
                      ? `${pogIndex + 1}-${bbox.shelf}-${bbox.column}`
                      : `${bbox.shelf}-${bbox.column}`;
                }

                return {
                  ...bbox,
                  ilSectionId: bbox.ilSectionId ?? pog.ilSectionId,
                  modCoverageId: bbox.modCoverageId ?? pog.modCoverageId,
                  pogImageUri: bbox.pogImageUri || pog.pogImageUri,
                  color,
                  shelfColumnLabel,
                  embeddingCropCount: bbox.embeddingCropCount,
                };
              });

              return (
                <div key={key} style={{ position: 'relative', minWidth: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      right: 8,
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      pointerEvents: 'none',
                    }}
                  >
                    <span
                      style={{
                        padding: '4px 7px',
                        borderRadius: 6,
                        border: '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(17,17,17,0.78)',
                        color: '#f0f6fc',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
                        fontSize: 11,
                        fontWeight: 900,
                        lineHeight: 1,
                        pointerEvents: 'none',
                      }}
                    >
                      POG {pogIndex + 1}
                    </span>
                  </div>
                  {pog?.pogImageUri ? (
                    <ImageViewer<PogBBox>
                      image={preloadedPogImageElement}
                      imageBase64={preloadedPogImage || pog.pogImageUri}
                      bboxes={pogBboxesWithColor}
                      onBoxClick={onPogBoxClick}
                      onBoxRightClick={
                        onPogBoxRightClick
                          ? (box) =>
                              onPogBoxRightClick({
                                ...(box as PogBBox),
                                ilSectionId: (box as PogBBox).ilSectionId ?? pog.ilSectionId,
                                modCoverageId: (box as PogBBox).modCoverageId ?? pog.modCoverageId,
                                pogImageUri: (box as PogBBox).pogImageUri || pog.pogImageUri,
                                previewImageBase64: preloadedPogImage?.startsWith('data:')
                                  ? preloadedPogImage
                                  : undefined,
                                previewImageElement: preloadedPogImageElement,
                              } as PogBBox)
                          : undefined
                      }
                      boxActions={[]}
                      showShelfColumnLabels={showShelfColumnLabels}
                      showEmbeddingsCount={showEmbeddingsCount}
                      showFallbackImages={showFallbackImages}
                      chainId={chainId}
                      imageBackgroundColor={imageBackgroundColor}
                      showFrameBorder={showImageFrameBorder}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 bg-gray-100 rounded">
                      <p className="text-gray-500 text-sm">No POG image available</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
